import { ApiClient } from '../ApiClient';

export interface PlaceBidRequest {
  auctionId: string;
  amount: number;
}

export interface BidResponse {
  id: string;
  auctionId: string;
  bidderId: string;
  amount: number;
  placedAt: string;
  status: string;
}

export interface BidHistoryResponse {
  bidderId: string;
  amount: number;
  placedAt: string;
  status: string;
}

export interface AuctionInfo {
  auctionId: string;
  currentPrice: number;
  currentLeaderId?: string;
  status: string;
  startsAt: string;
  endsAt: string;
  isActive: boolean;
  winnerId?: string;
  winningPrice?: number;
}

export interface AuctionStatus {
  hasAuction: boolean;
  isActive: boolean;
}

export class BiddingAPI {
  private static readonly BASE_URL = '/api/bids';
  private static readonly LISTING_BASE_URL = '/api/public/listings';

  /**
   * Places a bid on an auction
   */
  static async placeBid(request: PlaceBidRequest): Promise<BidResponse> {
    try {
      const response = await ApiClient.post<BidResponse>(
        `${this.BASE_URL}`,
        request,
        {
          requiresAuth: true,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to place bid:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to place bid');
    }
  }

  /**
   * Gets bid details by ID
   */
  static async getBidDetails(bidId: string): Promise<BidResponse> {
    try {
      const response = await ApiClient.get<BidResponse>(
        `${this.BASE_URL}/${bidId}`,
        {
          requiresAuth: false,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get bid details:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get bid details');
    }
  }

  /**
   * Gets bid history for an auction
   */
  static async getBidHistory(
    auctionId: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<BidHistoryResponse[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await ApiClient.get<BidHistoryResponse[]>(
        `${this.BASE_URL}/auction/${auctionId}?${params}`,
        {
          requiresAuth: false,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get bid history:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get bid history');
    }
  }

  /**
   * Gets user's bid history
   */
  static async getUserBidHistory(
    limit: number = 20,
    offset: number = 0
  ): Promise<BidHistoryResponse[]> {
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        offset: offset.toString(),
      });

      const response = await ApiClient.get<BidHistoryResponse[]>(
        `${this.BASE_URL}/user/history?${params}`,
        {
          requiresAuth: true,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get user bid history:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get user bid history');
    }
  }

  /**
   * Gets auction information for a listing
   */
  static async getListingAuction(listingId: string): Promise<AuctionInfo> {
    try {
      const response = await ApiClient.get<AuctionInfo>(
        `${this.LISTING_BASE_URL}/${listingId}/auction`,
        {
          requiresAuth: false,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get listing auction:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get listing auction');
    }
  }

  /**
   * Checks auction status for a listing
   */
  static async getListingAuctionStatus(listingId: string): Promise<AuctionStatus> {
    try {
      const response = await ApiClient.get<AuctionStatus>(
        `${this.LISTING_BASE_URL}/${listingId}/auction/status`,
        {
          requiresAuth: false,
        }
      );

      return response;
    } catch (error) {
      console.error('Failed to get auction status:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to get auction status');
    }
  }

  /**
   * Formats currency from number to display string
   */
  static formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  }

  /**
   * Calculates time remaining until auction ends
   */
  static getTimeRemaining(endsAt: string): string {
    const endTime = new Date(endsAt);
    const now = new Date();
    const diff = endTime.getTime() - now.getTime();

    if (diff <= 0) {
      return 'Ended';
    }

    const days = Math.floor(diff / (1000 * 60 * 60 * 24));
    const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

    if (days > 0) {
      return `${days}d ${hours}h ${minutes}m`;
    } else if (hours > 0) {
      return `${hours}h ${minutes}m`;
    } else {
      return `${minutes}m`;
    }
  }
}